import type { Document } from "@/api/models"

import { SettingsService } from "@/services/settings-service"

export type ParsedCitation = {
  source: string
  page: number | null
  quote: string | null
}

/**
 * A quoted field value inside a citation, e.g. `="exact text"`. It may contain
 * `]` and backslash escapes (`\"`), so it is matched as a unit.
 */
const QUOTED_CITATION_VALUE = String.raw`="(?:\\.|[^"\\\n])*"`

/**
 * `[[citation]]`, where a quoted value (`|quote="..."`) may contain `]`.
 * Outside a quoted value the citation ends at the first `]]`, exactly as
 * before. The alternatives are disjoint (a `=` either opens a complete quoted
 * value or is a plain character), so matching stays linear on long input.
 * Capture group 1 is the citation body.
 */
const CITATION_PATTERN_SOURCE = String.raw`\[\[((?:${QUOTED_CITATION_VALUE}|=(?!"(?:\\.|[^"\\\n])*")|[^\]=])+)\]\]`

/** A fresh global `[[citation]]` regex (global regexes carry `lastIndex` state). */
export function createCitationPattern(): RegExp {
  return new RegExp(CITATION_PATTERN_SOURCE, "g")
}

/** A fresh global regex for quoted citation values, e.g. to skip them while rewriting. */
export function createQuotedCitationValuePattern(): RegExp {
  return new RegExp(QUOTED_CITATION_VALUE, "g")
}

function normalizeLibraryCitationSource(source: string): string {
  // Backward/compat normalization:
  // Some model outputs use "library:<library_id>:<filename>".
  // Canonical source for this app is "<library_id>:<filename>".
  if (!source.startsWith("library:")) {
    return source
  }
  const withoutPrefix = source.slice("library:".length)
  return withoutPrefix.includes(":") ? withoutPrefix : source
}

function splitCitationFields(input: string): string[] {
  const parts: string[] = []
  let current = ""
  let inQuotes = false
  let escaped = false

  for (const char of input) {
    if (escaped) {
      current += char
      escaped = false
      continue
    }

    if (char === "\\") {
      current += char
      escaped = true
      continue
    }

    if (char === '"') {
      inQuotes = !inQuotes
      current += char
      continue
    }

    if (char === "|" && !inQuotes) {
      parts.push(current.trim())
      current = ""
      continue
    }

    current += char
  }

  parts.push(current.trim())
  return parts
}

function unescapeCitationValue(value: string): string {
  return value.replace(/\\([\\|"])/g, "$1")
}

function extractLegacyPageFromSource(source: string): { source: string; page: number | null } {
  const pageMatch = source.match(/#page=(\d+)/)
  if (!pageMatch) {
    return { source, page: null }
  }
  const page = parseInt(pageMatch[1], 10)
  return {
    source: source.split("#page=")[0].replace(/#$/, ""),
    page: Number.isNaN(page) ? null : page,
  }
}

/**
 * Parse citation string into source, page, and quote parts.
 * Supports: [[source]], [[source#page=N]], [[source|page=N|quote="..."]]
 */
export function parseCitationParts(citation: string): ParsedCitation {
  const cleanCitation = citation.split("#:~:text=")[0]
  const result: ParsedCitation = { source: cleanCitation, page: null, quote: null }

  if (!cleanCitation.includes("|")) {
    const pageMatch = cleanCitation.match(/#page=(\d+)/)
    if (pageMatch) {
      result.source = cleanCitation.split("#page=")[0].replace(/#$/, "")
      result.page = parseInt(pageMatch[1], 10)
    }
    result.source = normalizeLibraryCitationSource(result.source)
    return result
  }

  const parts = splitCitationFields(cleanCitation)
  const rawSource = normalizeLibraryCitationSource(unescapeCitationValue(parts[0] ?? "").trim())
  const legacy = extractLegacyPageFromSource(rawSource)
  result.source = legacy.source
  result.page = legacy.page
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i].trim()
    if (part.startsWith("page=")) {
      const num = parseInt(part.slice(5).trim(), 10)
      if (!Number.isNaN(num)) result.page = num
    } else if (part.startsWith('quote="')) {
      const rawQuote = part.slice(7)
      const unquoted = rawQuote.endsWith('"') ? rawQuote.slice(0, -1) : rawQuote
      result.quote = unescapeCitationValue(unquoted)
    }
  }
  return result
}

/**
 * Get the base citation (source only) for chunk lookup.
 * Strips |page=N|quote="..." and #page=N.
 */
export function getCitationSourceForLookup(citation: string): string {
  return normalizeLibraryCitationSource(parseCitationParts(citation).source)
}

/**
 * The short citation id a FILTH document was offered to the model under.
 *
 * A FILTH document's `sourcefile` is an opaque content handle hundreds of
 * characters long, which no model transcribes reliably. The backend offers the
 * model this alias instead and keeps the handle in
 * `extra_data.filth_content_handle`, which is where this client reads it.
 *
 * The handle is delivered on `extra_data.filth_content_handle`. A saved copy
 * omits the `sourcefile` path that repeats it; this client rebuilds
 * `filth-citation/{handle}/preview.txt`. A citation usually names an alias,
 * but a handle-shaped one is not impossible, so the lookup matches both.
 */
export function getDocumentCitationAlias(document: Document): string | null {
  const alias = document.extra_data?.filth_citation_alias
  return typeof alias === "string" && alias.trim() ? alias.trim() : null
}

/**
 * Case- and whitespace-insensitive key. Aliases are unique within one delivered
 * set under it, but not across two searches in the same turn, so a lookup can
 * still land on a same-named document from the other search.
 */
function citationMatchKey(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase()
}

const FILTH_CONTENT_HANDLE = /^(?![.]{1,2}$)[A-Za-z0-9._-]{1,4096}$/

function filthContentHandle(document: Document): string | null {
  const handle = document.extra_data?.filth_content_handle
  return typeof handle === "string" && FILTH_CONTENT_HANDLE.test(handle) ? handle : null
}

/** Path the content route serves. Persistence stores the handle once and omits this copy. */
function filthPreviewSourcefile(handle: string): string {
  return `filth-citation/${handle}/preview.txt`
}

function documentMatchesSource(libraryId: string, document: Document, source: string): boolean {
  const alias = getDocumentCitationAlias(document)
  if (alias && citationMatchKey(`${libraryId}:${alias}`) === citationMatchKey(source)) {
    return true
  }
  if (document.sourcefile && `${libraryId}:${document.sourcefile}` === source) {
    return true
  }
  const handle = filthContentHandle(document)
  return handle !== null && `${libraryId}:${filthPreviewSourcefile(handle)}` === source
}

/** Same match, for a citation the model wrote without its `library_id:` prefix. */
function documentAnswersToUnprefixedSource(document: Document, source: string): boolean {
  if (document.sourcefile === source) {
    return true
  }
  const alias = getDocumentCitationAlias(document)
  if (alias !== null && citationMatchKey(alias) === citationMatchKey(source)) {
    return true
  }
  const handle = filthContentHandle(document)
  return handle !== null && filthPreviewSourcefile(handle) === source
}

/**
 * Repair a model citation that omitted its library prefix when the returned
 * document metadata identifies exactly one library for the sourcefile.
 */
export function resolveLibraryCitationSource(
  citation: string,
  librariesAndDocuments: [string, Document][] | undefined
): string {
  const source = getCitationSourceForLookup(citation)
  if (source.includes(":") || !librariesAndDocuments?.length) {
    return source
  }

  const matchingLibraryIds = new Set(
    librariesAndDocuments
      .filter(([, document]) => documentAnswersToUnprefixedSource(document, source))
      .map(([libraryId]) => libraryId.trim())
      .filter(Boolean)
  )
  if (matchingLibraryIds.size !== 1) {
    return source
  }
  const [libraryId] = matchingLibraryIds
  return `${libraryId}:${source}`
}

function getLibraryDisplayName(citation: string): string | undefined {
  const source = getCitationSourceForLookup(citation)
  const libraryId = source.split(":")[0]
  const settingsService = SettingsService.getInstance()
  return settingsService.settings.indexes.find((index) => index.id === libraryId)?.display_name
}

/** Match library search document metadata to a citation (`libraryId:sourcefile`). */
export function findLibraryDocumentForCitation(
  citation: string,
  librariesAndDocuments: [string, Document][] | undefined
): Document | null {
  if (!librariesAndDocuments?.length) {
    return null
  }
  const source = resolveLibraryCitationSource(citation, librariesAndDocuments)
  const match = librariesAndDocuments.find(([libraryId, document]) =>
    documentMatchesSource(libraryId, document, source)
  )
  return match?.[1] ?? null
}

export function formatCitationDisplay(
  citation: string,
  librariesAndDocuments?: [string, Document][]
) {
  const { page: parsedPage } = parseCitationParts(citation)
  const source = resolveLibraryCitationSource(citation, librariesAndDocuments)
  const cleanCitation = citation.split("#:~:text=")[0]

  if (source.startsWith("doc:")) {
    const documentId = source.slice(4)
    return {
      fileNameWithoutPage: `Documents: ${documentId}`,
      fileNameWithPage: parsedPage
        ? `Documents: ${documentId} (Page ${parsedPage})`
        : `Documents: ${documentId}`,
      pageNumber: parsedPage,
    }
  }

  const [libraryId, ...filenameParts] = source.split(":")
  const filename = filenameParts.join(":")
  const filenameWithoutPage = filename.split("#page=")[0]
  const libraryName = getLibraryDisplayName(cleanCitation) || libraryId

  const doc = findLibraryDocumentForCitation(citation, librariesAndDocuments)
  const filenameForDisplay = doc?.original_sourcefile?.trim() || filenameWithoutPage

  const pageMatch = cleanCitation.match(/#page=(\d+)/)
  const pageNumber = parsedPage ?? (pageMatch ? parseInt(pageMatch[1], 10) : null)

  const baseDisplay = `${libraryName}: ${filenameForDisplay}`
  return {
    fileNameWithoutPage: baseDisplay,
    fileNameWithPage: pageNumber ? `${baseDisplay} (Page ${pageNumber})` : baseDisplay,
    pageNumber,
  }
}

export type CitationDisplayMeta = {
  title: string
  subtitle: string | null
  page: number | null
}

/**
 * Resolve structured display metadata (title, subtitle, page) for any citation type.
 * - web: hostname as title, full URL as subtitle
 * - m365: formatted display name as title, M365 sub-type as subtitle
 * - library/doc/file: cleaned file name as title, library/prefix label as subtitle
 */
export function resolveCitationDisplayMeta(
  citation: string,
  librariesAndDocuments?: [string, Document][]
): CitationDisplayMeta {
  const source = getCitationSourceForLookup(citation)

  if (source.startsWith("http://") || source.startsWith("https://")) {
    try {
      const url = new URL(source)
      return { title: url.hostname, subtitle: source, page: null }
    } catch {
      return { title: source, subtitle: null, page: null }
    }
  }

  if (source.startsWith("m365:")) {
    const { displayName, type } = formatM365Citation(citation)
    const subtitle = type ? type.charAt(0).toUpperCase() + type.slice(1) : null
    return { title: displayName, subtitle, page: null }
  }

  const { fileNameWithoutPage, pageNumber } = formatCitationDisplay(citation, librariesAndDocuments)
  const colonIndex = fileNameWithoutPage.indexOf(": ")

  if (colonIndex !== -1) {
    const prefix = fileNameWithoutPage.substring(0, colonIndex)
    const extractedTitle = fileNameWithoutPage.substring(colonIndex + 2).trim()
    if (extractedTitle) {
      return { title: extractedTitle, subtitle: prefix || null, page: pageNumber }
    }
    return { title: prefix, subtitle: null, page: pageNumber }
  }

  return { title: fileNameWithoutPage, subtitle: null, page: pageNumber }
}

/**
 * Resolve a human-readable display title from a citation string.
 * Strips the "LibraryName: " prefix when present (e.g. "My Lib: file.pdf" → "file.pdf").
 * For web citations returns the hostname; for M365 returns the formatted display name.
 */
export function resolveCitationDisplayTitle(
  citation: string,
  librariesAndDocuments?: [string, Document][]
): string {
  return resolveCitationDisplayMeta(citation, librariesAndDocuments).title
}

export type M365Type = "email" | "file" | "event" | "teams"

export function getM365Type(citation: string): M365Type | null {
  const cleanCitation = citation.split("#:~:text=")[0]
  const parts = cleanCitation.split(":")

  if (parts[0] !== "m365" || parts.length < 3) {
    return null
  }

  const type = parts[1]
  if (type === "email" || type === "file" || type === "event" || type === "teams") {
    return type
  }

  return null
}

export function formatM365Citation(citation: string): {
  type: M365Type | null
  displayName: string
  identifier: string
} {
  const cleanCitation = citation.split("#:~:text=")[0]
  const parts = cleanCitation.split(":")

  if (parts[0] !== "m365" || parts.length < 3) {
    return {
      type: null,
      displayName: citation,
      identifier: "",
    }
  }

  const type = parts[1] as M365Type
  const identifier = parts.slice(2).join(":")

  let displayName = citation
  switch (type) {
    case "email":
      displayName = "Email"
      break
    case "file":
      displayName = identifier
      break
    case "event":
      displayName = "Calendar Event"
      break
    case "teams":
      displayName = "Teams Conversation"
      break
  }

  return {
    type,
    displayName,
    identifier,
  }
}
